import React from 'react'
import { Dialog } from '@mui/material'
import { CloseRounded, UploadFileOutlined, CheckCircleRounded, DownloadRounded, WarningAmberRounded } from '@mui/icons-material'
import { api } from '../../function/axios'
import { Kind, KIND_LABEL, KIND_ONE, PROBLEM, VALID, detectKind, keyOf, normalize, pickValues, splitRows } from './blacklistRules'
import s from './SystemV2.module.css'

/*
 * Import into the blacklists — a .txt / .csv file or pasted text, into one
 * list or sorted by what each value looks like (Auto). Everything is checked
 * here first with the same rules as the one-by-one form (blacklistRules) and
 * shown before anything is sent: what will be added, what is on the list
 * already, repeats, and what does not fit and why. Then each list goes to
 * POST /blacklist/import (the server checks again, skips what is listed by
 * then, and inserts the rest at once), up to 5,000 values a request.
 */

const CHUNK = 5000
const MAX_FILE = 5 * 1024 * 1024

type Target = Kind | 'auto'
type Bad = { value: string; reason: string }
type Plan = { add: Record<Kind, string[]>; already: number; repeats: number; bad: Bad[]; total: number }
type Result = { added: number; already: number; repeats: number; bad: Bad[] }

function plan(text: string, target: Target, existing: Record<Kind, Set<string>>): Plan {
  const add: Record<Kind, string[]> = { email: [], phone: [], ip: [] }
  const seen: Record<Kind, Set<string>> = { email: new Set(), phone: new Set(), ip: new Set() }
  let already = 0
  let repeats = 0
  const bad: Bad[] = []
  const { values, emptyRows } = pickValues(splitRows(text), target)
  emptyRows.forEach((row) => bad.push({ value: row, reason: `no ${target === 'auto' ? 'email, phone or IP' : KIND_ONE[target]} in this row` }))
  values.forEach((raw) => {
    const kind = target === 'auto' ? detectKind(raw) : target
    if (!kind) { bad.push({ value: raw, reason: 'not an email, a phone or an IP' }); return }
    const v = normalize(kind, raw)
    if (!VALID[kind](v)) { bad.push({ value: raw, reason: `${KIND_ONE[kind]}: ${PROBLEM[kind]}` }); return }
    const k = keyOf(kind, v)
    if (existing[kind].has(k)) { already++; return }
    if (seen[kind].has(k)) { repeats++; return }
    seen[kind].add(k)
    add[kind].push(v)
  })
  return { add, already, repeats, bad, total: values.length + emptyRows.length }
}

function downloadCsv(name: string, rows: Bad[]) {
  const esc = (v: string) => `"${v.replace(/"/g, '""')}"`
  const csv = ['value,reason', ...rows.map((r) => `${esc(r.value)},${esc(r.reason)}`)].join('\n')
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }))
  const a = document.createElement('a')
  a.href = url
  a.download = name
  a.click()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

export function ImportBlacklistV2({ initialKind, existing, onClose, onDone }: {
  initialKind: Kind
  /** what each list holds now (the values as stored) */
  existing: Record<Kind, string[]>
  onClose: () => void
  /** after an import — the lists that changed, to reload */
  onDone: (kinds: Kind[]) => void
}) {
  const [target, setTarget] = React.useState<Target>(initialKind)
  const [text, setText] = React.useState('')
  const [fileName, setFileName] = React.useState('')
  const [fileError, setFileError] = React.useState('')
  const [over, setOver] = React.useState(false)
  const [step, setStep] = React.useState<'input' | 'review' | 'sending' | 'done'>('input')
  const [progress, setProgress] = React.useState<[number, number]>([0, 0])
  const [result, setResult] = React.useState<Result | null>(null)
  const [sendError, setSendError] = React.useState('')
  const fileRef = React.useRef<HTMLInputElement>(null)

  const existingKeys = React.useMemo(() => ({
    email: new Set(existing.email.map((v) => keyOf('email', v))),
    phone: new Set(existing.phone.map((v) => keyOf('phone', v))),
    ip: new Set(existing.ip.map((v) => keyOf('ip', v))),
  }), [existing])
  const p = React.useMemo(() => (step === 'input' ? null : plan(text, target, existingKeys)), [step, text, target, existingKeys])
  const toAdd = p ? p.add.email.length + p.add.phone.length + p.add.ip.length : 0

  function readFile(f?: File | null) {
    setFileError('')
    if (!f) return
    const ext = f.name.split('.').pop()?.toLowerCase() || ''
    if (!['txt', 'csv'].includes(ext)) { setFileError('A .txt or .csv file'); return }
    if (f.size > MAX_FILE) { setFileError('Up to 5 MB'); return }
    const reader = new FileReader()
    reader.onload = () => { setText(String(reader.result || '')); setFileName(f.name) }
    reader.onerror = () => setFileError('The file could not be read')
    reader.readAsText(f)
  }

  async function send() {
    if (!p) return
    const jobs: { kind: Kind; values: string[] }[] = []
    ;(['email', 'phone', 'ip'] as Kind[]).forEach((kind) => {
      for (let i = 0; i < p.add[kind].length; i += CHUNK) jobs.push({ kind, values: p.add[kind].slice(i, i + CHUNK) })
    })
    const total = jobs.reduce((n, j) => n + j.values.length, 0)
    const res: Result = { added: 0, already: p.already, repeats: p.repeats, bad: [...p.bad] }
    setStep('sending')
    setSendError('')
    setProgress([0, total])
    let sent = 0
    const touched = new Set<Kind>()
    try {
      for (const job of jobs) {
        // eslint-disable-next-line no-await-in-loop
        const { data } = await api({ method: 'POST', url: '/blacklist/import', body: job })
        res.added += Number(data?.added) || 0
        res.already += Number(data?.alreadyListed) || 0
        res.repeats += Number(data?.repeats) || 0
        ;(data?.invalid || []).forEach((x: Bad) => res.bad.push({ value: x.value, reason: `${KIND_ONE[job.kind]}: ${x.reason}` }))
        if (Number(data?.added)) touched.add(job.kind)
        sent += job.values.length
        setProgress([sent, total])
      }
    } catch (e: any) {
      setSendError(e?.response?.data?.message || e?.message || 'The import stopped')
    }
    setResult(res)
    setStep('done')
    onDone(Array.from(touched))
  }

  const busy = step === 'sending'
  return (
    <Dialog open onClose={busy ? undefined : onClose} disablePortal maxWidth={false} PaperProps={{ sx: { background: 'transparent', boxShadow: 'none', m: 2 } }}>
      <div className={s.importShell}>
        <div className={s.importTop}>
          <b>Import into blacklists</b>
          <button type="button" className={s.iconGhost} onClick={onClose} disabled={busy} aria-label="Close"><CloseRounded sx={{ fontSize: 18 }} /></button>
        </div>

        {step === 'input' && (
          <div className={s.importBody}>
            <div className={s.importRow}>
              <span className={s.muted}>Into</span>
              <div className={s.seg}>
                {(['email', 'phone', 'ip'] as Kind[]).map((k) => (
                  <button key={k} type="button" className={target === k ? s.segOn : ''} onClick={() => setTarget(k)}>{KIND_LABEL[k]}</button>
                ))}
                <button type="button" className={target === 'auto' ? s.segOn : ''} onClick={() => setTarget('auto')}>Auto</button>
              </div>
            </div>
            {target === 'auto' && <div className={s.hint}>Each value goes to the list it looks like: with an @ — emails; a + and digits — phones; digits with dots or colons — IP addresses.</div>}
            <div
              className={`${s.drop} ${over ? s.dropOver : ''}`}
              onDragOver={(e) => { e.preventDefault(); setOver(true) }}
              onDragLeave={() => setOver(false)}
              onDrop={(e) => { e.preventDefault(); setOver(false); readFile(e.dataTransfer.files?.[0]) }}
            >
              <button type="button" className={s.dropBtn} onClick={() => fileRef.current?.click()}>
                <UploadFileOutlined sx={{ fontSize: 22 }} />
                <b>{fileName || 'Drop a .txt or .csv file here or choose one'}</b>
                <span>{fileName ? 'Choose another' : 'one value per line, or separated by commas, semicolons or tabs'}</span>
              </button>
              <input ref={fileRef} type="file" accept=".txt,.csv" hidden onChange={(e) => { readFile(e.target.files?.[0]); e.target.value = '' }} />
            </div>
            {fileError && <div className={s.err}>{fileError}</div>}
            <textarea
              className={s.importText}
              value={text}
              onChange={(e) => { setText(e.target.value); setFileName('') }}
              placeholder={'…or paste the list here\nspam@example.com\n@tempmail.com\n+79001234567\n203.0.113.'}
              spellCheck={false}
            />
            <div className={s.editorBar}>
              <span className={s.muted}>Lines starting with # are skipped; in a CSV only the cells that look like {target === 'auto' ? 'an email, a phone or an IP' : `a ${KIND_ONE[target]}`} are taken.</span>
              <span className={s.spacer} />
              <button type="button" className={s.primary} onClick={() => setStep('review')} disabled={!text.trim()}>Check the list</button>
            </div>
          </div>
        )}

        {step === 'review' && p && (
          <div className={s.importBody}>
            <div className={s.importStats}>
              <div className={s.statOk}><b>{toAdd.toLocaleString()}</b><span>will be added</span></div>
              <div><b>{p.already.toLocaleString()}</b><span>already on the list</span></div>
              <div><b>{p.repeats.toLocaleString()}</b><span>repeats in the list</span></div>
              <div className={p.bad.length ? s.statBad : ''}><b>{p.bad.length.toLocaleString()}</b><span>do not fit</span></div>
            </div>
            {target === 'auto' && toAdd > 0 && (
              <div className={s.hint}>{(['email', 'phone', 'ip'] as Kind[]).filter((k) => p.add[k].length).map((k) => `${p.add[k].length.toLocaleString()} ${KIND_LABEL[k].toLowerCase()}`).join(' · ')}</div>
            )}
            {p.bad.length > 0 && (
              <div className={s.badList}>
                <div className={s.badHead}>
                  <WarningAmberRounded sx={{ fontSize: 16 }} />These are left out
                  <span className={s.spacer} />
                  <button type="button" className={s.ghostBtn} onClick={() => downloadCsv('blacklist-left-out.csv', p.bad)}><DownloadRounded sx={{ fontSize: 15 }} />Download</button>
                </div>
                {p.bad.slice(0, 50).map((b, i) => <div key={`${b.value}-${i}`} className={s.badRow}><code>{b.value}</code><span>{b.reason}</span></div>)}
                {p.bad.length > 50 && <div className={s.hint}>…and {(p.bad.length - 50).toLocaleString()} more in the download</div>}
              </div>
            )}
            <div className={s.editorBar}>
              <button type="button" className={s.ghostBtn} onClick={() => setStep('input')}>Back</button>
              <span className={s.spacer} />
              <button type="button" className={s.primary} onClick={() => { void send() }} disabled={!toAdd}>Add {toAdd.toLocaleString()} to the blacklist{toAdd === 1 ? '' : 's'}</button>
            </div>
          </div>
        )}

        {step === 'sending' && (
          <div className={s.importBody}>
            <div className={s.progressText}>Adding… {progress[0].toLocaleString()} / {progress[1].toLocaleString()}</div>
            <div className={s.progressBar}><i style={{ width: `${progress[1] ? (progress[0] / progress[1]) * 100 : 0}%` }} /></div>
            <div className={s.hint}>Keep this window open until it finishes.</div>
          </div>
        )}

        {step === 'done' && result && (
          <div className={s.importBody}>
            {sendError
              ? <div className={`${s.notice} ${s.noticeErr}`}><WarningAmberRounded sx={{ fontSize: 16 }} /><span>{sendError} — {result.added.toLocaleString()} were added before it stopped.</span></div>
              : <div className={`${s.notice} ${s.noticeOk}`}><CheckCircleRounded sx={{ fontSize: 16 }} /><span>Done.</span></div>}
            <div className={s.importStats}>
              <div className={s.statOk}><b>{result.added.toLocaleString()}</b><span>added</span></div>
              <div><b>{result.already.toLocaleString()}</b><span>already on the list</span></div>
              <div><b>{result.repeats.toLocaleString()}</b><span>repeats</span></div>
              <div className={result.bad.length ? s.statBad : ''}><b>{result.bad.length.toLocaleString()}</b><span>left out</span></div>
            </div>
            <div className={s.editorBar}>
              {result.bad.length > 0 && <button type="button" className={s.ghostBtn} onClick={() => downloadCsv('blacklist-left-out.csv', result.bad)}><DownloadRounded sx={{ fontSize: 15 }} />Download the left out</button>}
              <span className={s.spacer} />
              <button type="button" className={s.primary} onClick={onClose}>Close</button>
            </div>
          </div>
        )}
      </div>
    </Dialog>
  )
}
